import * as React from 'react';
import { useInView } from 'react-intersection-observer';

import { IconSolidPlayCircle } from '@notino/react-styleguide';

import ErrorBoundary from '@components/ErrorBoundary';
import { RectanglePlaceholder } from '@components/Placeholders';
import { isActivationKey } from '@helpers/accessibility/isActivationKey';
import { IImageProperties } from '@helpers/utils';

import { Border, Div, Img, TopMostDiv, VideoIcon } from './styled';

export interface IImageProps extends IImageProperties {
  onClick?: React.MouseEventHandler<HTMLElement>;
  onMouseOver?: React.EventHandler<React.MouseEvent<HTMLElement>>;
  onMouseOut?: React.EventHandler<React.MouseEvent<HTMLElement>>;
  isSelected: boolean;
  thumbSize?: string;
}

export const Image: React.FC<IImageProps> = ({
  isVideo,
  onClick,
  onMouseOver,
  onMouseOut,
  thumbSize,
  ...imageAttributes
}) => {
  const [ref, inView] = useInView({ triggerOnce: true });

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (!isActivationKey(e)) {
        return;
      }

      e.preventDefault();
      e.currentTarget.click();
    },
    []
  );

  return (
    <ErrorBoundary>
      <TopMostDiv>
        {imageAttributes.isSelected && <Border />}

        <Div
          ref={ref}
          onMouseOver={onMouseOver}
          onMouseOut={onMouseOut}
          role={onClick ? 'button' : undefined}
          tabIndex={onClick ? 0 : -1}
          onClick={onClick}
          onKeyDown={onClick ? handleKeyDown : undefined}
        >
          {inView ? (
            <Img
              data-testid="slider-image"
              {...imageAttributes}
              thumbSize={thumbSize}
            />
          ) : (
            <RectanglePlaceholder size={thumbSize || '8.75rem'} />
          )}
          {isVideo && (
            <VideoIcon>
              <IconSolidPlayCircle
                width="24px"
                height="24px"
                color="icon.primary"
              />
            </VideoIcon>
          )}
        </Div>
      </TopMostDiv>
    </ErrorBoundary>
  );
};
